// Build Line - profiles.
//
// A profile is the ROUTING CARD an order is stamped from: which Cloudflare
// account holds the zones, which registrar delegates them, which panel bucket
// the seats come out of, what the sequencer is configured to, what the mailboxes
// get tagged with, whether MasterInbox and photos are on by default.
//
// EDITING ONE DOES NOT TOUCH A RUNNING ORDER. Enrollment freezes the profile
// into the order's snapshot and the coordinator reads only that (design 4.2), so
// this form is safe to open mid-build. The one place an edit does bite is the
// preflight fingerprint: it folds in the profile's `updated_at`, so saving a
// profile invalidates any passing preflight taken against it and the order has
// to be re-flown. That is the honest answer - those gates were proven for a
// profile that no longer exists.
//
// SECURITY: no credential lives on a profile. `cf_account_id` is a reference to
// a cloudflare_accounts row whose token is already encrypted there. The probe
// panel below takes the customer's sequencer and MasterInbox keys, sends them
// once and never stores or echoes them - it is how an operator READS the
// MasterInbox workspace id the order snapshot has to declare.

import { useCallback, useEffect, useRef, useState } from 'react';

import type { BuildShowToast } from './BuildLine';
import './ProfileScreens.css';
import { PanelSplitFields } from './PanelSplitFields';
import { panelSplitLabel, validSerkenPercent } from './panel-split';
import {
  COLD_LABS_INSTANTLY_SETTINGS,
  MI_ALLOWED_APP_ID,
  REGISTRARS,
  buildApi,
  buildErrorMessage,
  buildIssues,
  duplicateProfileInput,
  emptyProfileInput,
  hostsAtRegistrar,
  previewRecordsFor,
  profileToInput,
  registrarDnsOf,
  type BuildAllocationPolicy,
  type BuildCfAccount,
  type BuildDnsMode,
  type BuildEnrollmentIssue,
  type BuildExtraRecord,
  type BuildProbeReport,
  type BuildProfile,
  type BuildProfileInput,
  type BuildRegistrar,
  type BuildRegistrarId,
  type BuildSequencerProvider,
  type PreviewGroup,
  type PreviewRecord,
} from './buildApi';

const EXTRA_TYPES: BuildExtraRecord['type'][] = ['TXT', 'CNAME', 'A', 'MX'];

/** A blank extra, appended by "Add record". TXT is the safe default: no proxied
 *  flag, no priority, and the operator picks the type first anyway. */
function blankExtra(): BuildExtraRecord {
  return { type: 'TXT', host: '', content: '', enabled: true };
}

/* ─── Sequencer settings: deep get/set over the same object the JSON edits ────
 * Both the structured form and the raw JSON textarea drive ONE object. These two
 * pure helpers are how a form field reaches a nested key without disturbing its
 * siblings; `setAt(obj, path, undefined)` REMOVES the leaf, which is how a
 * no-ftag customer is expressed (the key is absent, not empty). */
function getAt(obj: Record<string, unknown>, path: string[]): unknown {
  let cur: unknown = obj;
  for (const key of path) {
    if (typeof cur !== 'object' || cur === null) return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

function setAt(obj: Record<string, unknown>, path: string[], value: unknown): Record<string, unknown> {
  const head = path[0];
  if (head === undefined) return obj;
  const clone: Record<string, unknown> = { ...obj };
  if (path.length === 1) {
    if (value === undefined) delete clone[head];
    else clone[head] = value;
    return clone;
  }
  const child = clone[head];
  const childObj = typeof child === 'object' && child !== null && !Array.isArray(child)
    ? (child as Record<string, unknown>)
    : {};
  clone[head] = setAt(childObj, path.slice(1), value);
  return clone;
}

/** Where the zone comes from when DNS is hosted on Cloudflare. */
const CF_SOURCES: { value: BuildDnsMode; label: string; blurb: string }[] = [
  { value: 'cf_held', label: 'cf_held', blurb: 'the zones already sit in our Cloudflare account; no registrar is ever called' },
  { value: 'our_pool', label: 'our_pool', blurb: 'we register and route the domains ourselves' },
  { value: 'customer_cf', label: 'customer_cf', blurb: "the zones live in the customer's own Cloudflare account" },
];

/** The registrar a NEW registrar-DNS profile starts on: the one live-proven. */
const DEFAULT_DNS_REGISTRAR: BuildRegistrarId = 'spaceship';

const POLICIES: BuildAllocationPolicy[] = ['leave_0_or_2', 'exact_fill'];
const PROVIDERS: BuildSequencerProvider[] = ['instantly', 'smartlead', 'plusvibe', 'bison', 'none'];

/** A profile needs a Cloudflare account for every mode except our_pool and
 *  registrar DNS - the route refuses it otherwise. */
function needsCfAccount(mode: BuildDnsMode): boolean {
  return mode !== 'our_pool' && !hostsAtRegistrar(mode);
}

function registrarLabel(id: BuildRegistrar): string {
  return REGISTRARS.find((r) => r.value === id)?.label ?? id;
}

function pretty(value: Record<string, unknown>): string {
  return JSON.stringify(value, null, 2);
}

export function Profiles({ showToast }: { showToast: BuildShowToast }): JSX.Element {
  const [profiles, setProfiles] = useState<BuildProfile[]>([]);
  const [accounts, setAccounts] = useState<BuildCfAccount[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // The form. `editingId === null` with a form open means "create".
  const [form, setForm] = useState<BuildProfileInput | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  // The settings JSON is held as TEXT, not as a parsed object: parsing on every
  // keystroke would delete the operator's half-typed brace. `seq` is the LAST
  // VALID parse, which the structured sequencer form reads and writes; the two
  // are kept in sync - a form field edit rewrites the text, and the raw textarea
  // commits back into `seq` on blur (parse-guarded, never silently dropped).
  const [settingsText, setSettingsText] = useState('{}');
  const [seq, setSeq] = useState<Record<string, unknown>>({});
  const [seqJsonError, setSeqJsonError] = useState<string | null>(null);
  const [rawOpen, setRawOpen] = useState(false);
  const [standardDraft, setStandardDraft] = useState('');
  const [optionalDraft, setOptionalDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [issues, setIssues] = useState<BuildEnrollmentIssue[]>([]);
  // Which profile the probe panel is aimed at. Held here rather than inside the
  // panel so the per-row Probe button can aim it.
  const [probeId, setProbeId] = useState<number | null>(null);
  const screenRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const editing = form !== null;

  useEffect(() => {
    screenRef.current?.closest<HTMLElement>('.bl-scroll')?.scrollTo({ top: 0, behavior: 'auto' });
    headingRef.current?.focus({ preventScroll: true });
  }, [editing, editingId]);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const [rows, cf] = await Promise.all([
        buildApi.listProfiles(),
        // A failure here must not take the profile list with it: the picker is a
        // convenience, and a profile can be read without one.
        buildApi.listCloudflareAccounts().catch(() => [] as BuildCfAccount[]),
      ]);
      setProfiles(rows);
      setAccounts(cf);
      setLoadError(null);
    } catch (err) {
      setLoadError(buildErrorMessage(err));
    } finally {
      setLoaded(true);
    }
  }, []);

  // No polling: nothing in the app writes these rows except this form.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  function loadForm(input: BuildProfileInput, id: number | null): void {
    setForm(input);
    setEditingId(id);
    setSeq(input.sequencer_settings);
    setSettingsText(pretty(input.sequencer_settings));
    setSeqJsonError(null);
    // The raw JSON is the ONLY sequencer editor for a non-instantly provider, so
    // it starts open there and collapsed for instantly (which has the form).
    setRawOpen(input.sequencer_provider !== 'instantly');
    setStandardDraft('');
    setOptionalDraft('');
    setFormError(null);
    setIssues([]);
  }

  function openCreate(): void {
    loadForm(emptyProfileInput(), null);
  }

  function openEdit(profile: BuildProfile): void {
    loadForm(profileToInput(profile), profile.id);
  }

  function openDuplicate(profile: BuildProfile): void {
    loadForm(duplicateProfileInput(profile, profiles), null);
  }

  function closeForm(): void {
    setForm(null);
    setEditingId(null);
    setFormError(null);
    setIssues([]);
  }

  /** One field at a time, so the form never rebuilds an object it did not mean
   *  to (a spread of a stale `form` inside an async handler is how toggles lose
   *  each other's edits). */
  function patch(change: Partial<BuildProfileInput>): void {
    setForm((current) => (current === null ? current : { ...current, ...change }));
  }

  /** A structured sequencer field wrote a new settings object. Rewrite the JSON
   *  text from it so the raw editor and the form can never disagree, and clear a
   *  stale parse error the edit has just made irrelevant. */
  function applySeq(next: Record<string, unknown>): void {
    setSeq(next);
    setSettingsText(pretty(next));
    setSeqJsonError(null);
  }

  /** Commit the raw JSON textarea back into `seq` on blur. A parse error is
   *  SHOWN, never dropped: the last valid object stays and save() re-reports the
   *  same error against the same text. */
  function commitRawJson(): void {
    const trimmed = settingsText.trim();
    if (trimmed === '') {
      applySeq({});
      return;
    }
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setSeqJsonError('The sequencer settings must be a JSON object.');
        return;
      }
      setSeq(parsed as Record<string, unknown>);
      setSeqJsonError(null);
    } catch (err) {
      setSeqJsonError(`Not valid JSON: ${buildErrorMessage(err)}`);
    }
  }

  function setExtras(next: BuildExtraRecord[]): void {
    if (form === null) return;
    patch({ records: { ...form.records, extra_records: next } });
  }

  function updateExtra(index: number, change: Partial<BuildExtraRecord>): void {
    if (form === null) return;
    setExtras(form.records.extra_records.map((r, i) => (i === index ? { ...r, ...change } : r)));
  }

  async function save(): Promise<void> {
    if (form === null) return;
    setFormError(null);
    setIssues([]);
    if (form.panel_bucket === 'mixed' && !validSerkenPercent(form.serken_percent ?? 50)) {
      setFormError('Enter a whole Serken percentage from 0 to 100.');
      return;
    }

    // Parsed here so a stray comma reports with the browser's own position
    // information, rather than as a 400 from a server that only saw a string.
    let settings: Record<string, unknown>;
    try {
      const parsed: unknown = JSON.parse(settingsText.trim() === '' ? '{}' : settingsText);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setFormError('The sequencer settings must be a JSON object.');
        return;
      }
      settings = parsed as Record<string, unknown>;
    } catch (err) {
      setFormError(`The sequencer settings are not valid JSON: ${buildErrorMessage(err)}`);
      return;
    }

    const payload: BuildProfileInput = {
      ...form,
      serken_percent: validSerkenPercent(form.serken_percent) ? form.serken_percent : 50,
      sequencer_settings: settings,
      // The route refuses a cf_account_id on our_pool as surely as it refuses a
      // missing one elsewhere; send what the mode actually means.
      cf_account_id: needsCfAccount(form.dns_mode) ? form.cf_account_id : null,
    };

    setSaving(true);
    try {
      const saved = editingId === null
        ? await buildApi.createProfile(payload)
        : await buildApi.updateProfile(editingId, payload);
      showToast('success', `Profile "${saved.name}" saved.`);
      closeForm();
      await refresh();
    } catch (err) {
      setIssues(buildIssues(err));
      setFormError(buildErrorMessage(err));
      showToast('error', buildErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function addTag(kind: 'standard_tags' | 'optional_tags', raw: string): void {
    const tag = raw.trim();
    if (tag === '' || form === null) return;
    const current = form.tag_scheme[kind];
    if (current.includes(tag)) return;
    patch({ tag_scheme: { ...form.tag_scheme, [kind]: [...current, tag] } });
  }

  function removeTag(kind: 'standard_tags' | 'optional_tags', tag: string): void {
    if (form === null) return;
    patch({
      tag_scheme: { ...form.tag_scheme, [kind]: form.tag_scheme[kind].filter((t) => t !== tag) },
    });
  }

  const accountName = (id: number | null): string => {
    if (id === null) return 'none';
    const row = accounts.find((a) => a.id === id);
    return row ? `${row.name} (#${row.id})` : `#${id}`;
  };

  /** One numeric sequencer setting. An empty box REMOVES the key (the setting
   *  falls back to the server's canonical default), matching the ftag rule. */
  function seqNumField(id: string, label: string, path: string[]): JSX.Element {
    const value = getAt(seq, path);
    return (
      <div className="bl-field">
        <label className="bl-field-label" htmlFor={id}>{label}</label>
        <input
          id={id}
          className="bl-input bl-mono bl-input--num"
          type="number"
          inputMode="numeric"
          value={typeof value === 'number' ? String(value) : ''}
          onChange={(e) => applySeq(setAt(seq, path, e.target.value === '' ? undefined : Number(e.target.value)))}
        />
      </div>
    );
  }

  /** One boolean sequencer setting. */
  function seqBoolField(id: string, label: string, path: string[]): JSX.Element {
    return (
      <div className="bl-field">
        <label className="bl-check" htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            checked={getAt(seq, path) === true}
            onChange={(e) => applySeq(setAt(seq, path, e.target.checked))}
          />
          {label}
        </label>
      </div>
    );
  }

  // The ftag toggle IS the presence of the key: a customer with no ftag leaves it
  // off the object entirely, so a NEW instantly profile (settings {}) shows off.
  const ftagRaw = getAt(seq, ['warmup', 'warmup_custom_ftag']);
  const ftagOn = ftagRaw !== undefined;
  const ftagValue = typeof ftagRaw === 'string' ? ftagRaw : '';

  return (
    <div className="bl-profile-screen" ref={screenRef}>
      {loadError && <div className="bl-toast bl-toast--error">Could not load profiles: {loadError}</div>}

      {form === null ? (
        <>
      <section className="bl-card">
        <div className="bl-card-header">
          <h2 className="bl-card-title" ref={headingRef} tabIndex={-1}>Profiles</h2>
          <div className="bl-actions">
            <button type="button" className="bl-btn bl-btn--ghost bl-btn--small" onClick={() => void refresh()}>
              Refresh
            </button>
            <button type="button" className="bl-btn bl-btn--primary bl-btn--small" onClick={openCreate}>
              New profile
            </button>
          </div>
        </div>

        {profiles.length === 0 ? (
          <div className="bl-empty">
            {!loaded
              ? 'Loading profiles...'
              : loadError !== null
                ? 'The profile list could not be loaded - this is not an empty list.'
                : 'No profiles yet. An enrollment needs one, so start here.'}
          </div>
        ) : (
          <div className="bl-table-wrap">
            <table className="bl-table">
              <thead>
                <tr>
                  <th>Profile</th>
                  <th>Customer</th>
                  <th>DNS</th>
                  <th>Cloudflare</th>
                  <th>Registrar</th>
                  <th>Panels</th>
                  <th>Sequencer</th>
                  <th>Tags</th>
                  <th className="bl-col-center">MI</th>
                  <th className="bl-col-center">Photos</th>
                  <th className="bl-col-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((profile) => (
                  <tr key={profile.id}>
                    <td>
                      <strong>{profile.name}</strong>
                    </td>
                    <td>{profile.customer}</td>
                    <td className="bl-cell-mono">{profile.dns_mode}</td>
                    <td className="bl-cell-mono">{accountName(profile.cf_account_id)}</td>
                    <td className="bl-cell-mono">{registrarDnsOf(profile) ?? profile.registrar_default}</td>
                    <td className="bl-cell-mono">
                      {panelSplitLabel(profile)}
                      <div className="bl-cell-faint">{profile.allocation_policy}</div>
                    </td>
                    <td className="bl-cell-mono">{profile.sequencer_provider}</td>
                    <td className="bl-cell-faint">
                      {profile.tag_scheme.standard_tags.length > 0
                        ? profile.tag_scheme.standard_tags.join(', ')
                        : 'none'}
                      {profile.tag_scheme.group_tags_enabled && <div>+ group tags</div>}
                    </td>
                    <td className="bl-col-center">
                      <span className={`bl-badge bl-badge--${profile.masterinbox.enabled_default ? 'ok' : 'muted'}`}>
                        {profile.masterinbox.enabled_default ? 'on' : 'off'}
                      </span>
                    </td>
                    <td className="bl-col-center">
                      <span className={`bl-badge bl-badge--${profile.photos.enabled_default ? 'ok' : 'muted'}`}>
                        {profile.photos.enabled_default ? 'on' : 'off'}
                      </span>
                    </td>
                    <td className="bl-col-center">
                      <div className="bl-rowactions">
                        <button
                          type="button"
                          className="bl-btn bl-btn--secondary bl-btn--small"
                          onClick={() => openEdit(profile)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="bl-btn bl-btn--secondary bl-btn--small"
                          onClick={() => openDuplicate(profile)}
                        >
                          Duplicate
                        </button>
                        <button
                          type="button"
                          className="bl-btn bl-btn--ghost bl-btn--small"
                          onClick={() => setProbeId(profile.id)}
                        >
                          Probe
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {profiles.length > 0 && (
        <ProbePanel
          profiles={profiles}
          profileId={probeId}
          onProfileChange={setProbeId}
          showToast={showToast}
        />
      )}

        </>
      ) : (
        <>
        <nav className="bl-profile-back" aria-label="Profile navigation">
          <button type="button" className="bl-btn bl-btn--ghost" disabled={saving} onClick={closeForm}>
            <span aria-hidden="true">←</span> Back to profiles
          </button>
        </nav>
        <section className="bl-card bl-profile-editor">
          <div className="bl-card-header">
            <h2 className="bl-card-title" ref={headingRef} tabIndex={-1}>
              {editingId === null ? 'New profile' : `Edit profile ${form.name || editingId}`}
            </h2>
            <div className="bl-actions">
              <button type="button" className="bl-btn bl-btn--ghost bl-btn--small" disabled={saving} onClick={closeForm}>
                Cancel
              </button>
              <button
                type="button"
                className="bl-btn bl-btn--primary bl-btn--small"
                disabled={saving}
                onClick={() => void save()}
              >
                {saving ? 'Saving...' : 'Save profile'}
              </button>
            </div>
          </div>
          <div className="bl-card-body">
            {formError !== null && (
              <div className="bl-notice bl-notice--err">
                <strong>Refused:</strong> {formError}
              </div>
            )}
            {issues.length > 0 && (
              <div className="bl-notice bl-notice--err">
                {issues.map((issue, i) => (
                  <div key={`${issue.path}-${i}`}>
                    <code>{issue.path}</code> - {issue.message}
                  </div>
                ))}
              </div>
            )}
            {editingId !== null && (
              <div className="bl-notice">
                Each new order copies these settings when you enroll it.
              </div>
            )}

            <div className="bl-formgrid">
              <div className="bl-field">
                <label className="bl-field-label" htmlFor="bl-pf-name">Name</label>
                <input
                  id="bl-pf-name"
                  className="bl-input"
                  value={form.name}
                  onChange={(e) => patch({ name: e.target.value })}
                  placeholder="cold-labs"
                />
                <span className="bl-hint">Unique. This is what an operator picks at enrollment.</span>
              </div>
              <div className="bl-field">
                <label className="bl-field-label" htmlFor="bl-pf-customer">Customer</label>
                <input
                  id="bl-pf-customer"
                  className="bl-input"
                  value={form.customer}
                  onChange={(e) => patch({ customer: e.target.value })}
                  placeholder="Cold Labs"
                />
              </div>
            </div>

            <div className="bl-formgrid">
              <div className="bl-field">
                <label className="bl-field-label" htmlFor="bl-pf-hosting">DNS hosting</label>
                <select
                  id="bl-pf-hosting"
                  className="bl-input"
                  value={hostsAtRegistrar(form.dns_mode) ? 'registrar' : 'cloudflare'}
                  onChange={(e) => {
                    if (e.target.value === 'cloudflare') {
                      patch({ dns_mode: 'cf_held' });
                      return;
                    }
                    // Registrar DNS never touches a website: no Cloudflare account,
                    // no forwarding, no A or proxied extras (the server refuses them).
                    const current = form.registrar_default;
                    patch({
                      dns_mode: 'registrar', cf_account_id: null,
                      registrar_default: current === 'none' ? DEFAULT_DNS_REGISTRAR : current,
                      forwarding: { enabled: false, require_target_url: false },
                      records: { ...form.records, extra_records: form.records.extra_records.map((record) => ({
                        ...record, proxied: false, enabled: record.type === 'A' ? false : record.enabled,
                      })) },
                    });
                  }}
                >
                  <option value="cloudflare">Cloudflare</option>
                  <option value="registrar">Registrar DNS</option>
                </select>
                <span className="bl-hint">
                  {hostsAtRegistrar(form.dns_mode)
                    ? 'Add mail DNS at the registrar using the credential supplied at enrollment. Keep existing nameservers and website records.'
                    : 'Zones live on Cloudflare; when a registrar is set, Build Line points the domains at the zone.'}
                </span>
              </div>
              {!hostsAtRegistrar(form.dns_mode) && (
                <div className="bl-field">
                  <label className="bl-field-label" htmlFor="bl-pf-dns">Zone source</label>
                  <select
                    id="bl-pf-dns"
                    className="bl-input"
                    value={form.dns_mode}
                    onChange={(e) => patch({ dns_mode: e.target.value as BuildDnsMode })}
                  >
                    {CF_SOURCES.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  <span className="bl-hint">
                    {CF_SOURCES.find((m) => m.value === form.dns_mode)?.blurb}
                  </span>
                </div>
              )}
              {needsCfAccount(form.dns_mode) ? (
                <div className="bl-field">
                  <label className="bl-field-label" htmlFor="bl-pf-cf">Cloudflare account</label>
                  <select
                    id="bl-pf-cf"
                    className="bl-input"
                    value={form.cf_account_id === null ? '' : String(form.cf_account_id)}
                    onChange={(e) =>
                      patch({ cf_account_id: e.target.value === '' ? null : Number(e.target.value) })}
                  >
                    <option value="">select an account</option>
                    {accounts.map((a) => (
                      <option key={a.id} value={String(a.id)} disabled={a.is_active}>
                        {a.name} (#{a.id}){a.is_active ? ' - active, cannot be used' : ''}
                      </option>
                    ))}
                  </select>
                  <span className="bl-hint">
                    Required for <code>{form.dns_mode}</code>. It must be an inactive row: the active
                    accounts ARE the order-purchase rotation pool, and a build account joining it
                    would eventually have us creating our own zones inside a customer&apos;s
                    Cloudflare.
                  </span>
                </div>
              ) : (
                <div className="bl-field">
                  <span className="bl-field-label">Cloudflare account</span>
                  <span className="bl-hint">
                    {hostsAtRegistrar(form.dns_mode)
                      ? `No Cloudflare account needed. Enter the customer’s ${registrarLabel(registrarDnsOf(form) ?? DEFAULT_DNS_REGISTRAR)} credential when enrolling an order.`
                      : 'Our pool resolves its account at build time, so a profile does not pin one.'}
                  </span>
                </div>
              )}
              {hostsAtRegistrar(form.dns_mode) ? (
                <div className="bl-field">
                  <label className="bl-field-label" htmlFor="bl-pf-registrar">Registrar</label>
                  {/* A legacy `spaceship` row becomes `registrar` + the pick. */}
                  <select
                    id="bl-pf-registrar"
                    className="bl-input"
                    value={registrarDnsOf(form) ?? DEFAULT_DNS_REGISTRAR}
                    onChange={(e) => patch({ dns_mode: 'registrar', registrar_default: e.target.value as BuildRegistrarId })}
                  >
                    {REGISTRARS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  <span className="bl-hint">
                    The default registrar; an order may pick another at enrollment. Its credential is
                    used only for DNS records. Nameservers stay unchanged.
                  </span>
                </div>
              ) : (
                <div className="bl-field">
                  <label className="bl-field-label" htmlFor="bl-pf-registrar">Registrar for NS routing</label>
                  <select
                    id="bl-pf-registrar"
                    className="bl-input"
                    value={form.registrar_default}
                    onChange={(e) => patch({ registrar_default: e.target.value as BuildRegistrar })}
                  >
                    <option value="none">none</option>
                    {REGISTRARS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  <span className="bl-hint">
                    Registrar credentials are used when routing domains to Cloudflare. Existing Cloudflare zones need no registrar credential.
                  </span>
                </div>
              )}
            </div>

            <div className="bl-formgrid">
              <PanelSplitFields id="bl-pf" value={form} onChange={patch} />
              <div className="bl-field">
                <label className="bl-field-label" htmlFor="bl-pf-policy">Allocation policy</label>
                <select
                  id="bl-pf-policy"
                  className="bl-input"
                  value={form.allocation_policy}
                  onChange={(e) => patch({ allocation_policy: e.target.value as BuildAllocationPolicy })}
                >
                  {POLICIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <span className="bl-hint">
                  <code>leave_0_or_2</code> never strands a single unusable seat on a panel;{' '}
                  <code>exact_fill</code> packs them.
                </span>
              </div>
            </div>

            <div className="bl-section-title">DNS records</div>
            <div className="bl-formgrid">
              <div className="bl-field">
                <label className="bl-check" htmlFor="bl-pf-mx">
                  <input
                    id="bl-pf-mx"
                    type="checkbox"
                    checked={form.records.write_mx}
                    onChange={(e) => patch({ records: { ...form.records, write_mx: e.target.checked } })}
                  />
                  Write MX
                </label>
                <span className="bl-hint">
                  On by default. Without MX a built domain can send but never receive, which is the
                  gap the Px3 pipeline shipped with.
                </span>
              </div>
              <div className="bl-field">
                <label className="bl-field-label" htmlFor="bl-pf-spf">SPF</label>
                <input
                  id="bl-pf-spf"
                  className="bl-input bl-mono"
                  value={form.records.spf}
                  onChange={(e) => patch({ records: { ...form.records, spf: e.target.value } })}
                  placeholder="v=spf1 include:_spf.google.com ~all"
                />
                <span className="bl-hint">
                  Written as a TXT at the sending name. An override REPLACES this string - a second{' '}
                  <code>v=spf1</code> at one name is a permerror for the whole domain. Blank falls
                  back to the Google default.
                </span>
              </div>
              <div className="bl-field">
                <label className="bl-field-label" htmlFor="bl-pf-dmarc">DMARC policy</label>
                <input
                  id="bl-pf-dmarc"
                  className="bl-input bl-mono"
                  value={form.records.dmarc_policy}
                  onChange={(e) => patch({ records: { ...form.records, dmarc_policy: e.target.value } })}
                />
                <span className="bl-hint">
                  <code>&lt;collector&gt;</code> becomes one of each domain's own mailboxes, picked at random
                  at enrollment, e.g. <code>rua=mailto:&lt;collector&gt;</code>. The order page shows the pick.
                </span>
              </div>
              <div className="bl-field">
                <label className="bl-field-label" htmlFor="bl-pf-prefix">Tracking prefix</label>
                <input
                  id="bl-pf-prefix"
                  className="bl-input bl-mono"
                  value={form.records.tracking_prefix}
                  onChange={(e) => patch({ records: { ...form.records, tracking_prefix: e.target.value } })}
                />
                <span className="bl-hint">
                  The host the sequencer station attaches to each account (<code>{form.records.tracking_prefix || 'inst'}.&lt;domain&gt;</code>).
                  The CNAME itself is an extra below.
                </span>
              </div>
              <div className="bl-field">
                <label className="bl-field-label" htmlFor="bl-pf-target">Tracking target</label>
                <input
                  id="bl-pf-target"
                  className="bl-input bl-mono"
                  value={form.records.tracking_target}
                  onChange={(e) => patch({ records: { ...form.records, tracking_target: e.target.value } })}
                  placeholder="the sequencer's tracking host"
                />
              </div>
            </div>

            <div className="bl-field">
              <div className="bl-label-row">
                <span className="bl-field-label">Extra records</span>
                <button
                  type="button"
                  className="bl-btn bl-btn--secondary bl-btn--small"
                  onClick={() => setExtras([...form.records.extra_records, blankExtra()])}
                >
                  Add record
                </button>
              </div>
              <span className="bl-hint">
                Everything beyond SPF, DMARC and MX. Each host is a template the planner expands per
                sending domain (<code>inst</code> becomes <code>inst.go</code> on a subdomain). A TXT
                uses the format required by the selected DNS provider; disabled rows are skipped.
              </span>
              {form.records.extra_records.length === 0 ? (
                <span className="bl-hint">No extra records.</span>
              ) : (
                <div className="bl-extras">
                  <div className="bl-extra-head">
                    <span>Type</span>
                    <span>Host</span>
                    <span>Content</span>
                    <span>Proxied / priority</span>
                    <span>On</span>
                    <span />
                  </div>
                  {form.records.extra_records.map((extra, i) => (
                    <div className="bl-extra-row" key={i}>
                      <select
                        className="bl-input"
                        aria-label="record type"
                        value={extra.type}
                        onChange={(e) => updateExtra(i, { type: e.target.value as BuildExtraRecord['type'] })}
                      >
                        {EXTRA_TYPES.map((t) => <option key={t} value={t} disabled={hostsAtRegistrar(form.dns_mode) && t === 'A'}>{t}</option>)}
                      </select>
                      <input
                        className="bl-input bl-mono"
                        aria-label="host"
                        value={extra.host}
                        placeholder="inst"
                        onChange={(e) => updateExtra(i, { host: e.target.value })}
                      />
                      <input
                        className="bl-input bl-mono"
                        aria-label="content"
                        value={extra.content}
                        placeholder="prox.itrackly.com"
                        onChange={(e) => updateExtra(i, { content: e.target.value })}
                      />
                      <div className="bl-extra-slot">
                        {(extra.type === 'A' || extra.type === 'CNAME') && (
                          <label className="bl-check">
                            <input
                              type="checkbox"
                              disabled={hostsAtRegistrar(form.dns_mode)}
                              checked={extra.proxied === true}
                              onChange={(e) => updateExtra(i, { proxied: e.target.checked })}
                            />
                            Proxied
                          </label>
                        )}
                        {extra.type === 'MX' && (
                          <input
                            className="bl-input bl-mono bl-input--num"
                            type="number"
                            aria-label="priority"
                            placeholder="priority"
                            value={typeof extra.priority === 'number' ? String(extra.priority) : ''}
                            onChange={(e) =>
                              updateExtra(i, { priority: e.target.value === '' ? undefined : Number(e.target.value) })}
                          />
                        )}
                      </div>
                      <label className="bl-check" aria-label="enabled">
                        <input
                          type="checkbox"
                          checked={extra.enabled}
                          onChange={(e) => updateExtra(i, { enabled: e.target.checked })}
                        />
                      </label>
                      <button
                        type="button"
                        className="bl-icon-btn"
                        aria-label="remove record"
                        onClick={() => setExtras(form.records.extra_records.filter((_, j) => j !== i))}
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bl-formgrid">
              <div className="bl-field">
                <label className="bl-check" htmlFor="bl-pf-fwd-enabled">
                  <input
                    id="bl-pf-fwd-enabled"
                    disabled={hostsAtRegistrar(form.dns_mode)}
                    type="checkbox"
                    checked={form.forwarding.enabled}
                    onChange={(e) => patch({ forwarding: { ...form.forwarding, enabled: e.target.checked } })}
                  />
                  Forwarding enabled
                </label>
                <span className="bl-hint">
                  {hostsAtRegistrar(form.dns_mode)
                    ? 'Existing website records and redirects are preserved. Forwarding is unavailable with registrar DNS.'
                    : 'Creates forwarding A records and a 301 redirect. Turn off to preserve the existing website setup.'}
                </span>
              </div>
              <div className="bl-field">
                <label
                  className={`bl-check${form.forwarding.enabled ? '' : ' bl-check--disabled'}`}
                  htmlFor="bl-pf-fwd-require"
                >
                  <input
                    id="bl-pf-fwd-require"
                    type="checkbox"
                    disabled={!form.forwarding.enabled}
                    checked={form.forwarding.require_target_url}
                    onChange={(e) =>
                      patch({ forwarding: { ...form.forwarding, require_target_url: e.target.checked } })}
                  />
                  Require a forwarding target URL
                </label>
                <span className="bl-hint">
                  Only meaningful when forwarding is enabled: an enrollment without a target URL is
                  then refused, and so is a launch.
                </span>
              </div>
            </div>

            <div className="bl-summary">
              <span className="bl-field-label">What lands on Cloudflare</span>
              <span className="bl-hint">
                The exact record set this profile produces, recomputed as you edit. TXT values are
                shown with their quotes, exactly as they reach Cloudflare; names use the same
                per-domain expansion the planner does.
              </span>
              <div className="bl-summary-grid">
                <RecordSummaryColumn
                  domain="example.com"
                  groups={previewRecordsFor(form.records, form.forwarding.enabled, 'example.com')}
                />
                <RecordSummaryColumn
                  domain="go.example.com"
                  groups={previewRecordsFor(form.records, form.forwarding.enabled, 'go.example.com')}
                />
              </div>
            </div>

            <div className="bl-section-title">Sequencer</div>
            <div className="bl-formgrid">
              <div className="bl-field">
                <label className="bl-field-label" htmlFor="bl-pf-provider">Provider</label>
                <select
                  id="bl-pf-provider"
                  className="bl-input"
                  value={form.sequencer_provider}
                  onChange={(e) => {
                    const next = e.target.value as BuildSequencerProvider;
                    patch({ sequencer_provider: next });
                    // Raw JSON is the only sequencer editor off instantly; open it.
                    if (next !== 'instantly') setRawOpen(true);
                  }}
                >
                  {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <span className="bl-hint">
                  <code>none</code> is a real product decision, not an omission: the station stamps
                  the order sequencer-skipped and the hard gates drop both sequencer legs.
                </span>
              </div>
            </div>

            {form.sequencer_provider === 'instantly' ? (
              <>
                <div className="bl-label-row">
                  <span className="bl-field-label">Instantly settings</span>
                  <button
                    type="button"
                    className="bl-btn bl-btn--ghost bl-btn--small"
                    onClick={() => applySeq(COLD_LABS_INSTANTLY_SETTINGS)}
                  >
                    Load the Cold Labs defaults
                  </button>
                </div>
                <span className="bl-hint">
                  The baseline every order built from this profile starts at; an enrollment may PATCH
                  individual keys on top of it. The Cold Labs defaults are the values proven 360 for
                  360 on 2026-08-13.
                </span>

                <span className="bl-subhead">Sending</span>
                <div className="bl-formgrid">
                  {seqNumField('bl-seq-daily', 'Daily limit', ['daily_limit'])}
                  {seqNumField('bl-seq-gap', 'Sending gap (min)', ['sending_gap'])}
                  {seqBoolField('bl-seq-ramp', 'Enable slow ramp', ['enable_slow_ramp'])}
                </div>

                <span className="bl-subhead">Warmup</span>
                <div className="bl-formgrid">
                  {seqNumField('bl-seq-wl', 'Limit', ['warmup', 'limit'])}
                  {seqNumField('bl-seq-wi', 'Increment', ['warmup', 'increment'])}
                  {seqNumField('bl-seq-wr', 'Reply rate (%)', ['warmup', 'reply_rate'])}
                </div>

                <span className="bl-subhead">Warmup advanced</span>
                <div className="bl-formgrid">
                  {seqNumField('bl-seq-open', 'Open rate (%)', ['warmup', 'advanced', 'open_rate'])}
                  {seqNumField('bl-seq-spam', 'Spam save rate (%)', ['warmup', 'advanced', 'spam_save_rate'])}
                  {seqNumField('bl-seq-imp', 'Important rate (%)', ['warmup', 'advanced', 'important_rate'])}
                </div>
                <div className="bl-formgrid">
                  {seqBoolField('bl-seq-warmctd', 'Warm CTD', ['warmup', 'advanced', 'warm_ctd'])}
                  {seqBoolField('bl-seq-read', 'Read emulation', ['warmup', 'advanced', 'read_emulation'])}
                  {seqBoolField('bl-seq-weekday', 'Weekday only', ['warmup', 'advanced', 'weekday_only'])}
                </div>

                <span className="bl-subhead">Warmup ftag</span>
                <div className="bl-field">
                  <label className="bl-check" htmlFor="bl-seq-ftag-on">
                    <input
                      id="bl-seq-ftag-on"
                      type="checkbox"
                      checked={ftagOn}
                      onChange={(e) =>
                        applySeq(setAt(seq, ['warmup', 'warmup_custom_ftag'], e.target.checked ? ftagValue : undefined))}
                    />
                    Use a custom warmup ftag
                  </label>
                  <span className="bl-hint">
                    Off removes <code>warmup_custom_ftag</code> from the settings entirely - the way a
                    no-ftag customer is expressed. The ftag is workspace-specific; check it against
                    the workspace you are building into.
                  </span>
                </div>
                {ftagOn && (
                  <div className="bl-field">
                    <label className="bl-field-label" htmlFor="bl-seq-ftag">Ftag value</label>
                    <input
                      id="bl-seq-ftag"
                      className="bl-input bl-mono"
                      value={ftagValue}
                      placeholder="qjv9ybew"
                      onChange={(e) => applySeq(setAt(seq, ['warmup', 'warmup_custom_ftag'], e.target.value))}
                    />
                  </div>
                )}

                <span className="bl-subhead">Custom tracking domain</span>
                <div className="bl-field">
                  <label className="bl-check" htmlFor="bl-seq-ctd">
                    <input
                      id="bl-seq-ctd"
                      type="checkbox"
                      checked={form.ctd_default}
                      onChange={(e) => patch({ ctd_default: e.target.checked })}
                    />
                    Attach a custom tracking domain (CTD)
                  </label>
                  <span className="bl-hint">
                    Instantly&apos;s per-account custom-tracking-domain assertion. Default for this
                    profile&apos;s orders; an enrollment can still flip it.
                  </span>
                </div>
              </>
            ) : (
              <div className="bl-notice bl-notice--info">
                The structured fields cover the Instantly provider.{' '}
                {form.sequencer_provider === 'none'
                  ? 'With no sequencer, these settings are unused.'
                  : 'Edit smartlead settings as raw JSON below.'}
              </div>
            )}

            <details
              className="bl-raw"
              open={rawOpen}
              onToggle={(e) => setRawOpen(e.currentTarget.open)}
            >
              <summary className="bl-raw-summary">Advanced (raw JSON)</summary>
              <div className="bl-raw-body">
                {seqJsonError !== null && (
                  <div className="bl-notice bl-notice--err">
                    <strong>Parse error:</strong> {seqJsonError} The form keeps the last valid
                    settings until this is fixed.
                  </div>
                )}
                <textarea
                  id="bl-pf-settings"
                  className="bl-textarea bl-textarea--code"
                  value={settingsText}
                  onChange={(e) => setSettingsText(e.target.value)}
                  onBlur={commitRawJson}
                  spellCheck={false}
                />
                <span className="bl-hint">
                  The same object the form edits, and the escape hatch for keys the form does not
                  cover. Editing here updates the form on blur; editing a form field rewrites this.
                </span>
              </div>
            </details>

            <div className="bl-section-title">Tag scheme</div>
            <div className="bl-field">
              <span className="bl-field-label">Standard tags</span>
              <div className="bl-chips">
                {form.tag_scheme.standard_tags.length === 0 && <span className="bl-hint">none</span>}
                {form.tag_scheme.standard_tags.map((tag) => (
                  <span key={tag} className="bl-chip">
                    {tag}
                    <button
                      type="button"
                      className="bl-chip-x"
                      onClick={() => removeTag('standard_tags', tag)}
                      aria-label={`remove ${tag}`}
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
              <div className="bl-inline-add">
                <input
                  className="bl-input"
                  value={standardDraft}
                  placeholder="PI_{DATE}"
                  onChange={(e) => setStandardDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    addTag('standard_tags', standardDraft);
                    setStandardDraft('');
                  }}
                />
                <button
                  type="button"
                  className="bl-btn bl-btn--secondary bl-btn--small"
                  onClick={() => {
                    addTag('standard_tags', standardDraft);
                    setStandardDraft('');
                  }}
                >
                  Add
                </button>
              </div>
              <span className="bl-hint">
                Applied to every order and shown LOCKED at enrollment. <code>{'{DATE}'}</code> is
                resolved at enrollment to the UTC date - <code>PI_{'{DATE}'}</code> becomes{' '}
                <code>PI_2026_08_30</code> - because the station writes the tag list verbatim, and an
                unresolved token would put a literal brace in the customer&apos;s workspace.
              </span>
            </div>
            <div className="bl-field">
              <span className="bl-field-label">Optional tags</span>
              <div className="bl-chips">
                {form.tag_scheme.optional_tags.length === 0 && <span className="bl-hint">none</span>}
                {form.tag_scheme.optional_tags.map((tag) => (
                  <span key={tag} className="bl-chip">
                    {tag}
                    <button
                      type="button"
                      className="bl-chip-x"
                      onClick={() => removeTag('optional_tags', tag)}
                      aria-label={`remove ${tag}`}
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
              <div className="bl-inline-add">
                <input
                  className="bl-input"
                  value={optionalDraft}
                  placeholder="Backup"
                  onChange={(e) => setOptionalDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    addTag('optional_tags', optionalDraft);
                    setOptionalDraft('');
                  }}
                />
                <button
                  type="button"
                  className="bl-btn bl-btn--secondary bl-btn--small"
                  onClick={() => {
                    addTag('optional_tags', optionalDraft);
                    setOptionalDraft('');
                  }}
                >
                  Add
                </button>
              </div>
              <span className="bl-hint">Offered as checkboxes at enrollment; an operator can add more there.</span>
            </div>
            <div className="bl-field">
              <label className="bl-check" htmlFor="bl-pf-group">
                <input
                  id="bl-pf-group"
                  type="checkbox"
                  checked={form.tag_scheme.group_tags_enabled}
                  onChange={(e) =>
                    patch({ tag_scheme: { ...form.tag_scheme, group_tags_enabled: e.target.checked } })}
                />
                Group tags
              </label>
              <span className="bl-hint">
                Each mailbox&apos;s roster group (Active, Backup, ...) becomes one extra tag on that
                account.
              </span>
            </div>

            <div className="bl-section-title">MasterInbox, photos</div>
            <div className="bl-formgrid">
              <div className="bl-field">
                <label className="bl-check" htmlFor="bl-pf-mi">
                  <input
                    id="bl-pf-mi"
                    type="checkbox"
                    checked={form.masterinbox.enabled_default}
                    onChange={(e) =>
                      patch({ masterinbox: { ...form.masterinbox, enabled_default: e.target.checked } })}
                  />
                  MasterInbox on by default
                </label>
                <span className="bl-hint">
                  OAuth app is pinned to <code>{MI_ALLOWED_APP_ID}</code> and is not editable: the
                  station refuses anything else as a permanent <code>mi_blocked_app</code>. An order
                  with MasterInbox on must also declare the integer workspace id at enrollment.
                </span>
              </div>
              <div className="bl-field">
                <label className="bl-check" htmlFor="bl-pf-photos">
                  <input
                    id="bl-pf-photos"
                    type="checkbox"
                    checked={form.photos.enabled_default}
                    onChange={(e) => patch({ photos: { ...form.photos, enabled_default: e.target.checked } })}
                  />
                  Photos on by default
                </label>
                <span className="bl-hint">
                  After delivery, every photographed mailbox&apos;s picture is set to visible to Anyone.
                </span>
              </div>
            </div>

            <div className="bl-field">
              <label className="bl-field-label" htmlFor="bl-pf-notes">Notes</label>
              <textarea
                id="bl-pf-notes"
                className="bl-textarea"
                value={form.notes}
                onChange={(e) => patch({ notes: e.target.value })}
                placeholder="The nuance an operator has to remember when they enroll on this profile."
              />
              <span className="bl-hint">Shown on the profile card at step 1 of the enrollment.</span>
            </div>
          </div>
        </section>
        </>
      )}
    </div>
  );
}

/* ─── DNS record summary ────────────────────────────────────────────────── */

/** The proxied/priority annotation for one summary row. Surfaces the one
 *  distinction that bites: a tracking CNAME MUST be dns-only, and a forwarding A
 *  MUST be proxied. */
function recTag(rec: PreviewRecord): JSX.Element | null {
  if (rec.type === 'MX' && typeof rec.priority === 'number') {
    return <span className="bl-rec-tag"> prio {rec.priority}</span>;
  }
  if (rec.type === 'A' || rec.type === 'CNAME') {
    if (rec.proxied === true) return <span className="bl-rec-tag bl-rec-tag--proxied"> proxied</span>;
    if (rec.proxied === false) return <span className="bl-rec-tag"> dns-only</span>;
  }
  return null;
}

/** One sample domain's full CF record set, grouped. Root and subdomain sit side
 *  by side so the operator sees how a host template expands both ways. */
function RecordSummaryColumn({ domain, groups }: { domain: string; groups: PreviewGroup[] }): JSX.Element {
  return (
    <div className="bl-summary-col">
      <div className="bl-summary-col-title">{domain}</div>
      {groups.map((group) => (
        <div className="bl-summary-group" key={group.key}>
          <div className="bl-summary-group-label">{group.label}</div>
          {group.records.length === 0 ? (
            <div className="bl-summary-empty">{group.key === 'extras' ? 'no enabled extras' : 'nothing'}</div>
          ) : (
            <div className="bl-recs">
              {group.records.map((rec, i) => (
                <div className="bl-rec" key={`${rec.type}-${rec.name}-${i}`}>
                  <span className="bl-rec-type">{rec.type}</span>
                  <span className="bl-rec-name">{rec.name}</span>
                  <span className="bl-rec-content">
                    {rec.content}
                    {recTag(rec)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {group.note !== undefined && <div className="bl-summary-note">{group.note}</div>}
        </div>
      ))}
    </div>
  );
}

/* ─── Probe ─────────────────────────────────────────────────────────────── */

/**
 * Read-only probes for one profile, plus (optionally) the customer keys the
 * operator is holding.
 *
 * NOTHING IS STORED. The keys go up in the request body, are used once and are
 * dropped with it - the response carries a workspace name and a workspace id,
 * never a key. The MasterInbox leg is the reason this exists at all: it is where
 * the operator reads the integer workspace id the order snapshot then has to
 * declare, and an order that declares none parks every mailbox permanently.
 */
function ProbePanel({
  profiles,
  profileId,
  onProfileChange,
  showToast,
}: {
  profiles: BuildProfile[];
  /** null = nobody has aimed it yet, so it shows the first profile. */
  profileId: number | null;
  onProfileChange: (id: number) => void;
  showToast: BuildShowToast;
}): JSX.Element {
  const [sequencerKey, setSequencerKey] = useState('');
  const [masterinboxKey, setMasterinboxKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<BuildProbeReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const profile = profiles.find((p) => p.id === profileId) ?? profiles[0];

  // A report belongs to the profile it was taken against. Aiming the panel at
  // another one (from here or from a row's Probe button) has to drop it, or the
  // rows read as that profile's answer.
  useEffect(() => {
    setReport(null);
    setError(null);
  }, [profileId]);

  async function run(): Promise<void> {
    if (!profile) return;
    setBusy(true);
    setError(null);
    setReport(null);
    try {
      const result = await buildApi.probeProfile(profile.id, {
        ...(sequencerKey.trim() ? { sequencer_api_key: sequencerKey.trim() } : {}),
        ...(masterinboxKey.trim() ? { masterinbox_api_key: masterinboxKey.trim() } : {}),
      });
      setReport(result);
      const legs = [
        result.cloudflare?.ok ?? true,
        result.sequencer?.ok ?? true,
        result.masterinbox?.ok ?? true,
      ];
      showToast(legs.every(Boolean) ? 'success' : 'warn', legs.every(Boolean)
        ? 'Every probed leg answered.'
        : 'A probed leg failed - read the rows below.');
    } catch (err) {
      setError(buildErrorMessage(err));
      showToast('error', buildErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bl-card">
      <div className="bl-card-header">
        <h2 className="bl-card-title">Probe a profile</h2>
        <div className="bl-actions">
          <button
            type="button"
            className="bl-btn bl-btn--primary bl-btn--small"
            disabled={busy || !profile}
            onClick={() => void run()}
          >
            {busy ? 'Probing...' : 'Run probe'}
          </button>
        </div>
      </div>
      <div className="bl-card-body">
        <span className="bl-hint">
          Read-only, and nothing is stored: the keys below are used once for this request and
          dropped with it. The Cloudflare leg always runs (listing the account&apos;s zones is a
          stronger token check than a verify call, because it proves the token can address THAT
          account). The MasterInbox leg is how you read the integer workspace id an enrollment has
          to declare.
        </span>

        <div className="bl-formgrid">
          <div className="bl-field">
            <label className="bl-field-label" htmlFor="bl-probe-profile">Profile</label>
            <select
              id="bl-probe-profile"
              className="bl-input"
              value={profile ? String(profile.id) : ''}
              onChange={(e) => onProfileChange(Number(e.target.value))}
            >
              {profiles.map((p) => (
                <option key={p.id} value={String(p.id)}>{p.name}</option>
              ))}
            </select>
          </div>
          {profile && profile.sequencer_provider !== 'none' && (
            <div className="bl-field">
              <label className="bl-field-label" htmlFor="bl-probe-seq">
                {profile.sequencer_provider} api key (optional)
              </label>
              <input
                id="bl-probe-seq"
                className="bl-input bl-mono"
                type="password"
                autoComplete="off"
                value={sequencerKey}
                onChange={(e) => setSequencerKey(e.target.value)}
              />
            </div>
          )}
          <div className="bl-field">
            <label className="bl-field-label" htmlFor="bl-probe-mi">MasterInbox api key (optional)</label>
            <input
              id="bl-probe-mi"
              className="bl-input bl-mono"
              type="password"
              autoComplete="off"
              value={masterinboxKey}
              onChange={(e) => setMasterinboxKey(e.target.value)}
            />
          </div>
        </div>

        {error !== null && (
          <div className="bl-notice bl-notice--err">
            <strong>The probe could not run:</strong> {error}
          </div>
        )}

        {report !== null && (
          <div className="bl-probe">
            {report.cloudflare ? <ProbeRow
              label="Cloudflare"
              ok={report.cloudflare.ok}
              detail={report.cloudflare.ok
                ? `account ${report.cloudflare.account_id ?? 'unknown'} - ${report.cloudflare.zones ?? 0} zone(s) visible`
                : report.cloudflare.error ?? 'failed'}
            />
            : <div className="bl-notice">Spaceship DNS access is checked during enrollment’s dry run.</div>}
            {report.sequencer !== null && (
              <ProbeRow
                label={`Sequencer (${report.sequencer.provider})`}
                ok={report.sequencer.ok}
                detail={report.sequencer.ok
                  ? `workspace ${report.sequencer.workspace ?? 'unnamed'}`
                  : report.sequencer.error ?? 'failed'}
              />
            )}
            {report.masterinbox !== null && (
              <ProbeRow
                label={`MasterInbox (${report.masterinbox.app_id})`}
                ok={report.masterinbox.ok}
                detail={report.masterinbox.ok
                  ? `workspace ${report.masterinbox.workspace_id ?? 'unknown'}`
                    + (report.masterinbox.workspace_name ? ` - ${report.masterinbox.workspace_name}` : '')
                    + ' - declare this integer at enrollment'
                  : report.masterinbox.error ?? 'failed'}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function ProbeRow({ label, ok, detail }: { label: string; ok: boolean; detail: string }): JSX.Element {
  return (
    <div className={`bl-probe-row ${ok ? 'bl-probe-row--ok' : 'bl-probe-row--bad'}`}>
      <span className="bl-probe-mark">{ok ? 'PASS' : 'FAIL'}</span>
      <span className="bl-probe-label">{label}</span>
      <span className="bl-probe-detail">{detail}</span>
    </div>
  );
}
